import { useTheme } from '@emotion/react';
import { useMemo, ReactNode, forwardRef } from 'react';
import { MapUiSize, MAPUI_SIZES } from '../../common/types';
import { ContextProps } from './FormControlContext';
import { withForm } from './withForm';

import ReactSelect, { Props, StylesConfig } from 'react-select';

export const SELECT_SIZES = MAPUI_SIZES;

type Option = { value: string | number; label: ReactNode };

export interface SelectProps extends Props, ContextProps {
  isFullWidth?: boolean;
  size?: MapUiSize;
  options?: Array<Option>;
  value?: string | number | null;
  onChange?: (newValue: string | number) => void;
}

const InternalSelect = forwardRef<any, SelectProps>(
  (
    {
      controlId,
      isCompleted,
      isInvalid,
      isDisabled,
      isVerified,
      size = 'md',
      value: valueProp,
      options = [],
      placeholder = 'Seleziona',
      isSearchable = false,
      onChange,
      ...rest
    }: SelectProps,
    ref
  ) => {
    const theme = useTheme();
    const { fontSize, ...restStyles } = theme.form.select[size] as any;
    const styles: StylesConfig = useMemo(
      () => ({
        container: (provided) => ({
          ...provided,
          ...theme.form.select.defaultStylesContainer,
          fontSize,
        }),
        indicatorSeparator: () => ({
          display: 'none',
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          '>div': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: 0,
            color: '#455a64',
          },
        }),
        placeholder: (provided) => ({
          ...provided,
          color: theme.colors.neutral300,
        }),
        option: (provided, state) => ({
          ...provided,
          color: theme.colors.text300,
          borderBottom: `0.5px solid ${theme.colors.grey300}`,
          ...(state.isSelected && {
            backgroundColor: theme.colors.interaction400,
          }),
          ...(state.isFocused && {
            backgroundColor: theme.colors.interaction400,
          }),
        }),
        menuList: (provided) => ({
          ...provided,
          'div:last-of-type': {
            borderBottom: 'none',
          },
        }),
        menu: (provided) => ({
          ...provided,
          boxShadow: 'none',
          margin: 0,
          border: `1px solid ${theme.colors.neutral500}`,
          borderRadius: '0 0 8px 8px',
          borderTop: 'none',
          ...(isDisabled && theme.form.select.isDisabled),
          ...(isCompleted && theme.form.select.isCompleted),
          ...(isVerified && theme.form.select.isVerified),
          ...(isInvalid && theme.form.select.isInvalid),
        }),
        control: (provided, state) => {
          delete provided.borderColor;
          delete provided.boxShadow;
          delete provided['&:hover'];

          return {
            ...provided,
            ...theme.form.select.defaultStyles,
            ...restStyles,
            ...(state.menuIsOpen && {
              borderBottom: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }),

            ...(isDisabled && theme.form.select.isDisabled),
            ...(isCompleted && theme.form.select.isCompleted),
            ...(isVerified && theme.form.select.isVerified),
            ...(isInvalid && theme.form.select.isInvalid),
          };
        },
        valueContainer: (provided) => ({
          ...provided,
          height: restStyles.height,
          padding: 0,
        }),
        input: (provided) => ({
          ...provided,
          margin: 0,
          padding: 0,
        }),
      }),
      [
        theme,
        fontSize,
        isDisabled,
        isCompleted,
        isVerified,
        isInvalid,
        restStyles,
      ]
    );

    const value = useMemo(() => {
      return options.find((el) => el.value === valueProp) || valueProp;
    }, [valueProp, options]);

    return (
      <ReactSelect
        ref={ref}
        styles={styles}
        isDisabled={isDisabled}
        {...rest}
        value={value}
        options={options}
        id={controlId}
        placeholder={placeholder}
        isSearchable={isSearchable}
        onChange={(newValue: Option) => onChange(newValue.value)}
      />
    );
  }
);

InternalSelect.displayName = 'Select';

export const Select = withForm<SelectProps>(InternalSelect);
